import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MailCheck, MailX } from "lucide-react";
import { PageHeader, Panel, StandingBadge } from "@/components/PageHeader";
import { api, errMsg, fmtDate, showPin } from "@/lib/api";

const NONE = { pin: "no_pin", gamls_id: "no_gamls_id", staff_id: "no_staff_id" };

const valueOf = (m, f) => {
  const v = f.builtin ? m[f.key] : (m.custom || {})[f.key];
  if (NONE[f.key] && !v && m[NONE[f.key]]) return "I don't have one";
  if (f.key === "pin") return showPin(v) || "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (f.type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(v || "")) return new Date(`${v}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return v == null ? "" : String(v);
};

const Section = ({ s, m }) => (
  <Panel title={s.title} testId={`member-detail-section-${s.id}`}>
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {s.fields.map((f) => (
        <div key={f.key} className={f.width === "full" || f.type === "textarea" ? "sm:col-span-2" : ""}>
          <dt className="text-[11px] uppercase tracking-wider text-gray-500">{f.label}</dt>
          <dd className={`text-sm font-medium break-words ${["pin", "member_id"].includes(f.type) ? "font-mono" : ""}`} data-testid={`member-detail-${f.key}`}>{valueOf(m, f) || <span className="text-gray-400">—</span>}</dd>
        </div>
      ))}
    </dl>
  </Panel>
);

const Summary = ({ m }) => (
  <div className="flex flex-wrap items-center gap-3 text-sm mb-6" data-testid="member-detail-summary">
    <StandingBadge standing={m.standing} testId="member-detail-standing" />
    <span className="inline-flex items-center gap-1.5" data-testid="member-detail-verified">
      {m.email_verified ? <><MailCheck className="w-4 h-4 text-emerald-600" />Account confirmed</> : <><MailX className="w-4 h-4 text-gray-400" />Not confirmed</>}
    </span>
    <span className="text-gray-500">Code by {m.verify_channel === "sms" ? "SMS" : "email"}</span>
    <span className="text-gray-500" data-testid="member-detail-created">Opened {fmtDate(m.created_at)}</span>
  </div>
);

export default function MemberAccountDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/member-accounts/${id}`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  return (
    <div data-testid="member-detail-page">
      <Link to="/member-accounts" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-[#E60000] mb-3" data-testid="member-detail-back"><ArrowLeft className="w-4 h-4" />Member Accounts</Link>
      {err ? <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="member-detail-error">{err}</div>
        : !data ? <div className="text-gray-500">Loading…</div> : <>
          <PageHeader eyebrow="Member account" title={data.member.name} description={data.member.email} />
          <Summary m={data.member} />
          <div className="space-y-6">{data.form.sections.map((s) => <Section key={s.id} s={s} m={data.member} />)}</div>
        </>}
    </div>
  );
}
